import { MessageActionProvider } from '@/app/providers';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import '@/features/chats/ui/Conversation/MessageBubble/messageBubble.scss';
import {
   TextCard,
   ImageCard,
   VideoCard,
   LocationCard,
   FileCard,
   AudioCard,
   ContactCard,
   MeetCard,
   DeletedMessageCard,
   GroupNotificationCard,
   MediaGroupCard,
} from '@/components';

/**
 * MessageCardRenderer mapping.
 * Pure renderer that maps msgType to the appropriate visual card.
 * Accepts an optional `config` prop that is passed directly to MessageActionProvider,
 * allowing callers to control visibility (hideAvatar, hideActions, allowedActions, etc.)
 * without any external provider wrapping.
 */
const MessageCardRenderer = memo(({ message, activeChatJid, config }) => {
   if (!message || Object.keys(message).length === 0) return null;
   const { msgType, deleteStatus } = message;
   const cardProps = { message, activeChatJid: activeChatJid || message.fromUserJid };

   const renderCard = () => {
      if (deleteStatus === 1) return <DeletedMessageCard {...cardProps} />;

      if (message.type === 'groupedMedia') return <MediaGroupCard message={message} activeChatJid={activeChatJid} />;

      switch (msgType) {
         case 'text':
         case 'auto_text':
            return <TextCard {...cardProps} />;
         case 'image':
            return <ImageCard {...cardProps} />;
         case 'video':
            return <VideoCard {...cardProps} />;
         case 'location':
            return <LocationCard {...cardProps} />;
         case 'file':
            return <FileCard {...cardProps} />;
         case 'audio':
         case 'audio_recorded':
            return <AudioCard {...cardProps} />;
         case 'contact':
            return <ContactCard {...cardProps} />;
         case 'meet':
            return <MeetCard {...cardProps} />;
         case 'groupProfileUpdated':
            return <GroupNotificationCard message={message} />;
         default:
            return null;
      }
   };

   return <MessageActionProvider config={config}>{renderCard()}</MessageActionProvider>;
});

MessageCardRenderer.displayName = 'MessageCardRenderer';

MessageCardRenderer.propTypes = {
   message: PropTypes.shape({
      msgId: PropTypes.string,
      msgBody: PropTypes.shape({
         message: PropTypes.string,
         mentionedUsersIds: PropTypes.arrayOf(PropTypes.string),
         replyTo: PropTypes.string,
      }),
      timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isHighlighted: PropTypes.bool,
      msgStatus: PropTypes.number,
      editMessageId: PropTypes.string,
      favouriteStatus: PropTypes.number,
      msgType: PropTypes.string,
      deleteStatus: PropTypes.number,
      fromUserJid: PropTypes.string,
      type: PropTypes.string,
   }).isRequired,
   activeChatJid: PropTypes.string,
   config: PropTypes.object,
};

export default MessageCardRenderer;
